import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    ViewChild
} from '@angular/core';
import {MatDialog} from "@angular/material/dialog";
import {CommunityStatistic, FeedPost} from '../_types/content';
import {
    combineLatest,
    distinctUntilChanged,
    EMPTY,
    map,
    Observable,
    retry,
    startWith,
    Subject,
    switchMap,
    take,
    tap
} from 'rxjs';
import {AuthService} from '../_services/auth.service';
import {User} from '../_types/user';
import {DashboardFeedComponent} from '../feed/dashboard-feed/dashboard-feed.component';
import {ContentService} from '../_services/content.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Community, CommunityPage, SubGroup} from '../_types/communities';
import {CommunitiesService} from '../_services/communities.service';
import {LevelsComponent} from "../shared/levels/levels.component";
import {BadgesComponent} from "../shared/badges/badges.component";
import {NotificationsListComponent} from "../shared/notifications-list/notifications-list.component";
import {PostsListComponent} from "../shared/posts-list/posts-list.component";
import {endOfDay, endOfMonth, formatDateToYYYYMMDD, startOfMonth} from '../_utils/date';
import {LocalStorageService} from '../_services/local-storage.service';
import {mostReadable, TinyColor} from "@ctrl/tinycolor";
import {ThemeService} from "../_services/theme.service";
import {EmblaCarouselDirective} from 'embla-carousel-angular';
import type {EmblaCarouselType} from 'embla-carousel';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DashboardComponent implements AfterViewInit {
    @ViewChild(DashboardFeedComponent) dashboardFeedComponent!: DashboardFeedComponent;
    @ViewChild(EmblaCarouselDirective) emblaRef!: EmblaCarouselDirective;
    @ViewChild('dotsNode') dotsNodeRef!: ElementRef<HTMLElement>;
    @ViewChild('nextBtn') nextBtnNode!: HTMLButtonElement
    @ViewChild('prevousBtn') prevBtnNode!: HTMLButtonElement
    pointsDate: string = 'month'
    currentCommunityId: number | null = null;
    loading: boolean | null = null;
    lastPosts?: FeedPost[] = [];
    subgroups: SubGroup[] = [];
    community: Community | null = null;
    communityStatistic!: CommunityStatistic;
    startDate?: string;
    endDate?: string;
    menuItems: any[] = []
    statsLoading: boolean | null = null;
    protected emblaApi: EmblaCarouselType | null = null;
    private reinitTimer?: number;
    private dotsEventsBound: boolean = false;
    private changeDate$: Subject<{ startDate?: string, endDate?: string }> = new Subject<{
        startDate?: string,
        endDate?: string
    }>();

    constructor(public dialog: MatDialog,
                private authService: AuthService,
                private contentService: ContentService,
                private communitiesService: CommunitiesService,
                private localStorageService: LocalStorageService,
                private destroyRef: DestroyRef,
                protected themeService: ThemeService,
                private cdr: ChangeDetectorRef
    ) {
        this.communitiesService.getCurrentDashboardCommunity()
            .pipe(
                switchMap(community => {
                    this.community = community;
                    if (community?.id) {
                        this.communitiesService.getCommunityPages(community?.id).subscribe(r => {
                            let listItems: CommunityPage[] = []
                            for (let item of r) {
                                if (item.display_in_useful_links) {
                                    listItems.push(item)
                                }
                            }
                            listItems?.sort((a, b) => a.order - b.order);

                            this.menuItems = [
                                ...(community?.badges_active && this.isCommunityMember ? [{
                                    title: community?.useful_info_badges_text_rendered || 'My badges',
                                    clickAction: () => this.openBadges(),
                                    backgroundColor: community?.useful_info_my_badges_color_hex,
                                    textColor: this.getContrastColor(community?.useful_info_my_badges_color_hex),
                                    borderIfWhite: community?.useful_info_my_badges_color_hex === '#ffffff'
                                }] : []),

                                ...(community?.levels_active && this.isCommunityMember ? [{
                                    title: community?.useful_info_levels_text_rendered || 'My levels',
                                    clickAction: () => this.openLevels(),
                                    backgroundColor: community?.useful_info_my_levels_color_hex,
                                    textColor: this.getContrastColor(community?.useful_info_my_levels_color_hex),
                                    borderIfWhite: community?.useful_info_my_levels_color_hex === '#ffffff'
                                }] : []),

                                {
                                    title: community?.useful_info_notifications_text_rendered || 'My notifications',
                                    clickAction: () => this.viewAllNotifications(),
                                    backgroundColor: community?.useful_info_my_notifications_color_hex,
                                    textColor: this.getContrastColor(community?.useful_info_my_notifications_color_hex),
                                    borderIfWhite: community?.useful_info_my_notifications_color_hex === '#ffffff'
                                },

                                {
                                    title: community?.useful_info_comments_posts_text_rendered || 'My comments and posts',
                                    clickAction: () => this.viewMyPosts(),
                                    backgroundColor: community?.useful_info_my_comments_posts_color_hex,
                                    textColor: this.getContrastColor(community?.useful_info_my_comments_posts_color_hex),
                                    borderIfWhite: community?.useful_info_my_comments_posts_color_hex === '#ffffff'
                                },

                                {
                                    title: community?.useful_info_posts_library_text_rendered || 'Posts library',
                                    clickAction: () => this.viewAllPosts(),
                                    backgroundColor: community?.useful_info_my_post_library_color_hex,
                                    textColor: this.getContrastColor(community?.useful_info_my_post_library_color_hex),
                                    borderIfWhite: community?.useful_info_my_post_library_color_hex === '#ffffff'
                                },

                                ...(community?.terms_and_conditions ? [{
                                    title: community?.useful_info_terms_text_rendered || 'Terms & conditions',
                                    routerLink: ['/terms-and-conditions'],
                                    backgroundColor: community?.useful_info_my_terms_color_hex,
                                    textColor: this.getContrastColor(community?.useful_info_my_terms_color_hex),
                                    borderIfWhite: community?.useful_info_my_terms_color_hex === '#ffffff'
                                }] : []),

                                ...(community?.privacy_policy ? [{
                                    title: community?.useful_info_privacy_text_rendered || 'Privacy policy',
                                    routerLink: ['/privacy-policy'],
                                    backgroundColor: community?.useful_info_my_privacy_color_hex,
                                    textColor: this.getContrastColor(community?.useful_info_my_privacy_color_hex),
                                    borderIfWhite: community?.useful_info_my_privacy_color_hex === '#ffffff'
                                }] : []),

                                {
                                    title: community?.useful_info_contact_text_rendered || 'Contact us',
                                    routerLink: ['/contact'],
                                    backgroundColor: community?.useful_info_my_contact_color_hex,
                                    textColor: this.getContrastColor(community?.useful_info_my_contact_color_hex),
                                    borderIfWhite: community?.useful_info_my_contact_color_hex === '#ffffff'
                                },

                                ...listItems.map(item => ({
                                    title: item.title,
                                    routerLink: ['/info', item.id],
                                    backgroundColor: item?.useful_info_menu_color_hex,
                                    textColor: this.getContrastColor(item?.useful_info_menu_color_hex),
                                    borderIfWhite: item?.useful_info_menu_color_hex === '#ffffff'
                                }))
                            ];
                        })
                    }
                    this.currentCommunityId = community?.id ?? null;
                    this.loadLastPosts(this.currentCommunityId);
                    return community?.id
                        ? this.getSubgroups(community.id)
                        : EMPTY;
                }),
                takeUntilDestroyed()
            )
            .subscribe();
    }

    get isCommunityMember(): boolean {
        return this.communitiesService.userHasCommunityRole(['member']);
    }

    get currentUser(): User {
        return this.authService.getUser();
    }

    get lastSelectedCommunity(): Community | null {
        return this.localStorageService.getItem<Community | null>('lastSelectedCommunity', null);
    }

    ngAfterViewInit(): void {
        this.listenCommunityStats();

    }

    openLevels(): void {
        this.dialog.open(LevelsComponent, {
            panelClass: 'mediumWindow',
            data: {user: this.currentUser, community: this.community}
        });
    }

    openBadges(): void {
        this.dialog.open(BadgesComponent, {panelClass: 'mediumWindow', maxHeight: '80vh'});
    }

    loadLastPosts(id: number | null): void {
        if (!id) return;
        setTimeout((): void => {
            // Delay loading wheel for 3 seconds.
            if (this.loading != false) {
                this.loading = true;
            }
        }, 2000);
        this.contentService.getFeed(id, 1, 6)
            .pipe(take(1), retry(1))
            .subscribe({
                next: result => {
                    this.lastPosts = result.results;
                    this.loading = false;
                    setTimeout((): void => {
                        this.emblaApi = this.emblaRef?.emblaApi

                        if (this.emblaApi) {
                            this.bindDotEventsOnce();
                            this.scheduleReinit();
                        }
                    }, 200);
                },
                error: () => this.loading = false
            });
    }

    viewAllNotifications(): void {
        this.dialog.open(NotificationsListComponent, {
            panelClass: 'appFixWindow',
        });
    }

    viewAllPosts(): void {
        this.dialog.open(PostsListComponent, {
            panelClass: 'appFixWindow',
        });
    }

    viewMyPosts(): void {
        this.dialog.open(PostsListComponent, {
            panelClass: 'appFixWindow',
            data: {
                myPosts: true
            }
        });
    }

    getUserPoints(event: 'allTime' | 'lastYear' | 'month'): void {
        this.pointsDate = event;
        const currentDate: Date = new Date();
        switch (event) {
            case "lastYear":
                this.startDate = formatDateToYYYYMMDD(new Date());
                const previousYearDate: Date = new Date(currentDate);
                previousYearDate.setFullYear(currentDate.getFullYear() - 1);
                this.endDate = formatDateToYYYYMMDD(endOfDay(previousYearDate)!);
                break;
            case "month":
                this.endDate = formatDateToYYYYMMDD(startOfMonth(new Date())!);
                this.startDate = formatDateToYYYYMMDD(endOfMonth(new Date())!);
                break;
            case "allTime":
                this.endDate = undefined;
                this.startDate = undefined;
                break;
        }
        this.changeDate$.next({startDate: this.startDate, endDate: this.endDate});
    }

    getContrastColor(color?: string): string | null {
        if (color) {
            let fallback: string = new TinyColor(color).isLight() ? 'rgba(black, 0.87)' : 'white';
            let mostReadableColor = mostReadable(color, ["#ffffff", "#000000", "#808080", "#C0C0C0"], {includeFallbackColors: true})?.toHexString()
            if (!mostReadableColor) {
                mostReadableColor = fallback;
            }
            return mostReadableColor
        } else {
            return null
        }
    }

    private scheduleReinit(): void {
        if (!this.emblaApi) return;

        if (this.reinitTimer) window.clearTimeout(this.reinitTimer);

        this.reinitTimer = window.setTimeout((): void => {
            requestAnimationFrame((): void => {
                this.emblaApi?.reInit();
                this.rebuildDots();
                this.updateDots();
            });
        }, 0);
    }

    private bindDotEventsOnce(): void {
        if (!this.emblaApi || this.dotsEventsBound) return;
        this.dotsEventsBound = true;

        const sync = (): void => {
            this.rebuildDots();
            this.updateDots();
        };

        this.emblaApi.on('init', sync);
        this.emblaApi.on('reInit', sync);
        this.emblaApi.on('select', sync);
    }

    private rebuildDots(): void {
        const dotsContainer: HTMLElement = this.dotsNodeRef?.nativeElement;
        const embla: EmblaCarouselType | null = this.emblaApi;
        if (!dotsContainer || !embla) return;

        dotsContainer.innerHTML = '';

        embla.scrollSnapList().forEach((_, index): void => {
            const dot: HTMLButtonElement = document.createElement('button');
            dot.classList.add('emblaDot');
            dot.type = 'button';
            dot.addEventListener('click', (): void => embla.scrollTo(index), false);
            dotsContainer.appendChild(dot);
        });
    }

    private updateDots(): void {
        const dotsContainer: HTMLElement = this.dotsNodeRef?.nativeElement;
        const embla: EmblaCarouselType | null = this.emblaApi;
        if (!dotsContainer || !embla) return;

        const selectedIndex: number = embla.selectedScrollSnap();
        Array.from(dotsContainer.children).forEach((dot, index): void => {
            dot.classList.toggle('emblaDotSelected', index === selectedIndex);
        });
        this.cdr.detectChanges();
    }

    private getSubgroups(id: number): Observable<SubGroup[]> {
        this.subgroups = [];
        return this.communitiesService.getSubGroups(id)
            .pipe(
                map(res => res || []),
                tap(res => this.subgroups = res)
            );
    }

    private listenCommunityStats(): void {
        setTimeout((): void => {
            // Delay loading wheel for 3 seconds.
            if (this.statsLoading != false) {
                this.statsLoading = true;
            }
        }, 2000);
        combineLatest([
            this.communitiesService.getCurrentDashboardCommunity().pipe(startWith(this.lastSelectedCommunity)),
            this.changeDate$.pipe(startWith({startDate: this.startDate, endDate: this.endDate}))
        ]).pipe(
            map(([community, trigger]) => ({community, trigger})),
            distinctUntilChanged((prev, cur) => {
                return prev.community?.id === cur.community?.id && prev.trigger?.startDate === cur.trigger?.startDate && prev.trigger?.endDate === cur.trigger?.endDate
            }),
            takeUntilDestroyed(this.destroyRef),
            switchMap(res => {
                if (!res.community) return EMPTY;
                return this.communitiesService.getCommunityStatistic(res.community.id, this.startDate, this.endDate)
            }),
        ).subscribe(res => {
            this.statsLoading = false;
            this.communityStatistic = res;
        });
    }
}
